import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AppLayout } from "./components/layout/AppLayout";
import { CookieConsent } from "./components/shared/CookieConsent";
import { useDeepLinks } from "./hooks/useDeepLinks";
import { useQuickActions } from "./hooks/useQuickActions";
import { isNativeApp } from "./lib/isNativeApp";
import { getRuntimeMode } from "./lib/runtimeMode";
import { RuntimeDebugBanner } from "./components/debug/RuntimeDebugBanner";
import { DebugLogOverlay } from "./components/debug/DebugLogOverlay";
import { ErrorBoundary } from "./components/debug/ErrorBoundary";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BiometricLockProvider, useBiometricLock } from "./contexts/BiometricLockContext";
import { DocumentPreviewProvider } from "./contexts/DocumentPreviewContext";
import { BiometricLockScreen } from "./components/auth/BiometricLockScreen";
import { OnboardingGuard } from "./components/auth/OnboardingGuard";
import { Loader2 } from "lucide-react";

// Pages
import Landing from "./pages/Landing";
import AppEntry from "./pages/AppEntry";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
// Dashboard removed - merged into Documents (Unified Home)
import Documents from "./pages/Documents";
import DocumentDetail from "./pages/DocumentDetail";
import Pricing from "./pages/Pricing";
import Onboarding from "./pages/Onboarding";
import PDFTools from "./pages/PDFTools";
import SharedDocument from "./pages/SharedDocument";
import SharedCollection from "./pages/SharedCollection";
import ShareLinks from "./pages/ShareLinks";
import Settings from "./pages/Settings";
import Notifications from "./pages/Notifications";
import Privacy from "./pages/Privacy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import IncidentResponsePolicy from "./pages/IncidentResponsePolicy";
import VulnerabilityDisclosurePolicy from "./pages/VulnerabilityDisclosurePolicy";
import InformationSecurityPolicy from "./pages/InformationSecurityPolicy";
import PrivacyPolicyEN from "./pages/PrivacyPolicyEN";
import IncidentResponsePolicyEN from "./pages/IncidentResponsePolicyEN";
import VulnerabilityDisclosurePolicyEN from "./pages/VulnerabilityDisclosurePolicyEN";
import InformationSecurityPolicyEN from "./pages/InformationSecurityPolicyEN";
import SecurityFAQ from "./pages/SecurityFAQ";
import Terms from "./pages/Terms";
import Legal from "./pages/Legal";

import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import NotFound from "./pages/NotFound";
// B2B Pages
import LegalDocuments from "./pages/LegalDocuments";
import Subcontractors from "./pages/Subcontractors";
import Tenders from "./pages/Tenders";
import TenderDetail from "./pages/TenderDetail";
// Individual Pages
import AdministrativeHealth from "./pages/AdministrativeHealth";
import SubcontractorPortal from "./pages/SubcontractorPortal";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import PropertyPortal from "./pages/PropertyPortal";
import Fleet from "./pages/Fleet";
import FleetDetail from "./pages/FleetDetail";
import FleetFines from "./pages/FleetFines";
import DriverDetail from "./pages/DriverDetail";
import NativeCallback from "./pages/NativeCallback";
import AppleCallback from "./pages/AppleCallback";
import Family from "./pages/Family";
import FamilyMemberHealth from "./pages/FamilyMemberHealth";
import Actions from "./pages/Actions";
import ActionsArchive from "./pages/ActionsArchive";
import ActionDetail from "./pages/ActionDetail";
import Demarches from "./pages/Demarches";
import DemarcheDetail from "./pages/DemarcheDetail";

const queryClient = new QueryClient();

const runtimeMode = getRuntimeMode();
const native = runtimeMode === "native" ? true : runtimeMode === "web" ? false : isNativeApp();

// Loading screen component
const LoadingScreen = ({ message = "Chargement…" }: { message?: string }) => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-3 text-muted-foreground">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
      <span className="text-sm">{message}</span>
    </div>
  </div>
);

// Hydrating screen (shown between auth and documents)
const HydratingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-3 text-muted-foreground">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
      <span className="text-sm">Chargement…</span>
    </div>
  </div>
);

// Native app home: redirect based on auth status
const NativeHome = () => {
  const { status, isHydrated } = useAuth();

  console.log("[NativeHome] Render:", { status, isHydrated });

  // Show loading while checking auth
  if (status === "loading") {
    return <LoadingScreen message="Vérification de la session…" />;
  }

  // Show hydrating screen while waiting for user data
  if (status === "hydrating" || (status === "authenticated" && !isHydrated)) {
    return <HydratingScreen />;
  }

  // Timeout reached, show app entry to let user login manually
  if (status === "timeout") {
    return <AppEntry />;
  }

  // Redirect to unified home (/documents) if authenticated and hydrated
  if (status === "authenticated" && isHydrated) {
    return <Navigate to="/documents" replace />;
  }

  // Unauthenticated
  return <AppEntry />;
};

// Component to handle deep links (must be inside BrowserRouter)
const DeepLinkHandler = () => {
  useDeepLinks();
  return null;
};

// Component to set up iOS Quick Actions
const QuickActionsHandler = () => {
  useQuickActions();
  return null;
};

// Biometric lock gate - shows lock screen when app is locked
const BiometricLockGate = () => {
  const { isLocked, unlock } = useBiometricLock();
  
  if (!isLocked) return null;
  
  return <BiometricLockScreen onUnlock={unlock} />;
};

// Main app routes
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={native ? <NativeHome /> : <Landing />} />
      {/* Force marketing landing access even in native/webview */}
      <Route path="/landing" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/callback/native" element={<AuthCallback />} />
      <Route path="/native-callback" element={<NativeCallback />} />
      <Route path="/callback/apple" element={<AppleCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Redirect /dashboard to unified home */}
      <Route path="/dashboard" element={<Navigate to="/documents" replace />} />
      <Route path="/documents" element={<OnboardingGuard><ErrorBoundary><Documents /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/demarches" element={<OnboardingGuard><ErrorBoundary><Demarches /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/demarches/:id" element={<OnboardingGuard><ErrorBoundary><DemarcheDetail /></ErrorBoundary></OnboardingGuard>} />
      {/* Actions page - separate from demarches */}
      <Route path="/actions" element={<OnboardingGuard><ErrorBoundary><Actions /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/actions/archive" element={<OnboardingGuard><ErrorBoundary><ActionsArchive /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/actions/:documentId" element={<OnboardingGuard><ErrorBoundary><ActionDetail /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/documents/:id" element={<OnboardingGuard><ErrorBoundary><DocumentDetail /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/legal-documents" element={<OnboardingGuard><ErrorBoundary><LegalDocuments /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/subcontractors" element={<OnboardingGuard><ErrorBoundary><Subcontractors /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/tenders" element={<OnboardingGuard><ErrorBoundary><Tenders /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/tenders/:id" element={<OnboardingGuard><ErrorBoundary><TenderDetail /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/portal/:token" element={<ErrorBoundary><SubcontractorPortal /></ErrorBoundary>} />
      <Route path="/immo" element={<OnboardingGuard><ErrorBoundary><Properties /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/immo/:id" element={<OnboardingGuard><ErrorBoundary><PropertyDetail /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/immo-portal/:token" element={<ErrorBoundary><PropertyPortal /></ErrorBoundary>} />
      <Route path="/fleet" element={<OnboardingGuard><ErrorBoundary><Fleet /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/fleet/:id" element={<OnboardingGuard><ErrorBoundary><FleetDetail /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/fleet/drivers/:id" element={<OnboardingGuard><ErrorBoundary><DriverDetail /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/fleet/fines" element={<OnboardingGuard><ErrorBoundary><FleetFines /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/administrative-health" element={<OnboardingGuard><ErrorBoundary><AdministrativeHealth /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/health" element={<Navigate to="/administrative-health" replace />} />
      <Route path="/family" element={<OnboardingGuard><ErrorBoundary><Family /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/family/:memberId" element={<OnboardingGuard><ErrorBoundary><FamilyMemberHealth /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/pricing" element={<ErrorBoundary><Pricing /></ErrorBoundary>} />
      <Route path="/onboarding" element={<ErrorBoundary><Onboarding /></ErrorBoundary>} />
      <Route path="/pdf-tools" element={<OnboardingGuard><ErrorBoundary><PDFTools /></ErrorBoundary></OnboardingGuard>} />
      {/* IMPORTANT: More specific routes must come BEFORE generic ones */}
      <Route path="/share/collection/:token" element={<ErrorBoundary><SharedCollection /></ErrorBoundary>} />
      <Route path="/share/:token" element={<ErrorBoundary><SharedDocument /></ErrorBoundary>} />
      <Route path="/collection/:token" element={<ErrorBoundary><SharedCollection /></ErrorBoundary>} />
      <Route path="/share-links" element={<OnboardingGuard><ErrorBoundary><ShareLinks /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/settings" element={<OnboardingGuard><ErrorBoundary><Settings /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/notifications" element={<OnboardingGuard><ErrorBoundary><Notifications /></ErrorBoundary></OnboardingGuard>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/incident-response-policy" element={<IncidentResponsePolicy />} />
      <Route path="/vulnerability-disclosure-policy" element={<VulnerabilityDisclosurePolicy />} />
      <Route path="/information-security-policy" element={<InformationSecurityPolicy />} />
      <Route path="/privacy-policy-en" element={<PrivacyPolicyEN />} />
      <Route path="/incident-response-policy-en" element={<IncidentResponsePolicyEN />} />
      <Route path="/vulnerability-disclosure-policy-en" element={<VulnerabilityDisclosurePolicyEN />} />
      <Route path="/information-security-policy-en" element={<InformationSecurityPolicyEN />} />
      <Route path="/security-faq" element={<SecurityFAQ />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/legal" element={<Legal />} />
      
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <BiometricLockProvider>
              <DocumentPreviewProvider>
                <BiometricLockGate />
                <RuntimeDebugBanner />
                <DebugLogOverlay />
                <DeepLinkHandler />
                <QuickActionsHandler />
                
                <AppLayout>
                  <AppRoutes />
                </AppLayout>
                <CookieConsent />
              </DocumentPreviewProvider>
            </BiometricLockProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
